
import React, { useState } from 'react';
import { ArrowLeft, Instagram, Video, Facebook, Mail, Package, Lightbulb, Sparkles, Copy, Trash2, Loader2, Wand2, ChevronDown, ChevronUp, Edit3, Save } from 'lucide-react';
import Toast from './Notifications';

// Simple loader component
const Loader = () => (
  <div className="loader ease-linear rounded-full border-8 border-t-8 border-gray-200 h-16 w-16"></div>
);

// Simple prompt generators for demonstration
const promptGenerators = {
  instagram: (data) => `Créez un post Instagram pour ${data.product}.

Rédigez un texte court et engageant (150 mots max) avec des emojis.
Adaptez le langage pour ${data.audience}.
Utilisez un ton ${data.tone}.
Terminez par un appel à l'action clair.
Objectif : ${data.goal}.

Le post doit être prêt à publier.`,

  facebook: (data) => `Créez une publicité Facebook pour ${data.product}.

Texte principal (125 caractères max) + description (30 mots max).
Ton : ${data.tone}.
Objectif : ${data.goal}.
Budget : ${data.budget}.
Public : ${data.audience}.

Incluez un appel à l'action et des suggestions visuelles.
La publicité doit être convaincante.`,

  tiktok: (data) => `Écrivez un script TikTok de ${data.duration} sur ${data.product}.

Structure :
1. Introduction accrocheuse (5-10 secondes)
2. Développement du sujet
3. Moment fort
4. Conclusion avec appel à l'action

Style : ${data.style}.
Ton : ${data.tone}.
Public : ${data.audience}.

Incluez le texte à afficher à l'écran.`,

  email: (data) => `Rédigez un email promotionnel pour ${data.product}.

Structure :
- Objet accrocheur
- Salutation
- Introduction engageante
- Présentation de l'offre : ${data.offer}
- Bénéfices pour le destinataire
- Appel à l'action
- Signature

Ton : ${data.tone}.
Objectif : ${data.goal}.
Destinataires : ${data.audience}.

L'email doit inciter à l'action immédiate.`,

  product: (data) => `Créez une description produit pour ${data.product}.

Incluez :
- Titre accrocheur
- Caractéristiques principales
- Bénéfices pour ${data.audience}
- Avantage unique : ${data.usp}
- Prix : ${data.price}
- Appel à l'action

Catégorie : ${data.category}.
La description doit être persuasive.`,

  contentIdeas: (data) => `Proposez 10 idées de contenu pour ${data.niche}.

Plateforme : ${data.platform}.
Public : ${data.audience}.
Fréquence : ${data.frequency}.
Objectif : ${data.goal}.

Incluez :
- Thèmes variés
- Types de format adaptés
- Suggestions de hashtags
- Calendrier sur 4 semaines

Les idées doivent être engageantes.`,

  caption: (data) => `Créez une légende pour ${data.product} sur ${data.platform}.

Longueur : ${data.length}.
Ton : ${data.tone}.
Objectif : ${data.goal}.
Public : ${data.audience}.

Incluez :
- Texte engageant
- Emojis appropriés
- Appel à l'action
- 3-5 hashtags pertinents

La légende doit être optimisée pour la plateforme.`
};



// Generator Page Component
const GeneratorPage = ({ onBack, onSavePrompt }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({});
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const categories = [
    { id: 'instagram', name: 'Post Instagram', icon: <Instagram className="w-6 h-6" />, color: 'from-purple-500 to-pink-500', description: 'Publication pour Instagram' },
    { id: 'facebook', name: 'Pub Facebook', icon: <Facebook className="w-6 h-6" />, color: 'from-blue-500 to-indigo-600', description: 'Annonce publicitaire' },
    { id: 'tiktok', name: 'Vidéo TikTok', icon: <Video className="w-6 h-6" />, color: 'from-black to-pink-500', description: 'Script de vidéo courte' },
    { id: 'email', name: 'Email marketing', icon: <Mail className="w-6 h-6" />, color: 'from-red-500 to-orange-500', description: 'Campagne par email' },
    { id: 'product', name: 'Description produit', icon: <Package className="w-6 h-6" />, color: 'from-green-500 to-emerald-600', description: 'Fiche produit' },
    { id: 'contentIdeas', name: 'Idées de contenu', icon: <Lightbulb className="w-6 h-6" />, color: 'from-yellow-400 to-orange-500', description: 'Calendrier éditorial' },
    { id: 'caption', name: 'Légende', icon: <Sparkles className="w-6 h-6" />, color: 'from-purple-400 to-blue-400', description: 'Texte d\'accompagnement' }
  ];

  // Champs essentiels (toujours visibles)
  const essentialFields = {
    instagram: [
      { name: 'product', label: 'Ton produit ou service', type: 'text', placeholder: 'Ex: Formation en ligne, Application mobile' },
      { name: 'goal', label: 'Objectif de ton post', type: 'select', options: ['Vendre', 'Engager', 'Éduquer', 'Divertir'] }
    ],
    facebook: [
      { name: 'product', label: 'Ton produit ou service', type: 'text', placeholder: 'Ex: Formation en ligne, Application mobile' },
      { name: 'goal', label: 'Objectif de ta pub', type: 'select', options: ['Vendre', 'Générer des contacts', 'Faire connaître', 'Attirer du trafic'] }
    ],
    tiktok: [
      { name: 'product', label: 'Ton sujet ou produit', type: 'text', placeholder: 'Ex: Recette rapide, Astuce beauté' },
      { name: 'goal', label: 'Objectif de ta vidéo', type: 'select', options: ['Éduquer', 'Divertir', 'Inspirer', 'Montrer'] }
    ],
    email: [
      { name: 'product', label: 'Ton produit ou offre', type: 'text', placeholder: 'Ex: Ebook gratuit, Formation' },
      { name: 'goal', label: 'Objectif de ton email', type: 'select', options: ['Vendre', 'Informer', 'Fidéliser', 'Convertir'] }
    ],
    product: [
      { name: 'product', label: 'Nom de ton produit', type: 'text', placeholder: 'Ex: Smart Watch Pro X, Crème hydratante' },
      { name: 'goal', label: 'Objectif de la description', type: 'select', options: ['Vendre', 'Informer', 'Convaincre'] }
    ],
    contentIdeas: [
      { name: 'niche', label: 'Ta thématique', type: 'text', placeholder: 'Ex: Marketing digital, Cuisine healthy' },
      { name: 'goal', label: 'Objectif du contenu', type: 'select', options: ['Éduquer', 'Convertir', 'Engager', 'Fidéliser'] }
    ],
    caption: [
      { name: 'product', label: 'Ton contenu ou produit', type: 'text', placeholder: 'Ex: Nouveau podcast, Produit lancé' },
      { name: 'goal', label: 'Objectif de la légende', type: 'select', options: ['Engager', 'Vendre', 'Informer', 'Divertir'] }
    ]
  };

  // Champs avancés (masqués par défaut)
  const advancedFields = {
    instagram: [
      { name: 'audience', label: 'Public visé', type: 'text', placeholder: 'Ex: Entrepreneurs, Parents, Étudiants' },
      { name: 'tone', label: 'Ton souhaité', type: 'select', options: ['Inspirant', 'Éducatif', 'Divertissant', 'Professionnel'] }
    ],
    facebook: [
      { name: 'audience', label: 'Public visé', type: 'text', placeholder: 'Ex: Entrepreneurs, Parents, Étudiants' },
      { name: 'tone', label: 'Ton souhaité', type: 'select', options: ['Professionnel', 'Conversationnel', 'Urgent', 'Éducatif'] },
      { name: 'budget', label: 'Budget prévu', type: 'text', placeholder: 'Ex: 500-1000€/mois' }
    ],
    tiktok: [
      { name: 'duration', label: 'Durée souhaitée', type: 'select', options: ['15 secondes', '30 secondes', '60 secondes', '3 minutes'] },
      { name: 'style', label: 'Style de vidéo', type: 'select', options: ['Tutoriel', 'Histoire', 'Humour', 'Tendance'] },
      { name: 'tone', label: 'Ton souhaité', type: 'select', options: ['Énergique', 'Décontracté', 'Éducatif', 'Inspirant'] },
      { name: 'audience', label: 'Public visé', type: 'text', placeholder: 'Ex: Jeunes adultes, Familles' }
    ],
    email: [
      { name: 'audience', label: 'Destinataires', type: 'text', placeholder: 'Ex: Abonnés newsletter, Clients' },
      { name: 'tone', label: 'Ton souhaité', type: 'select', options: ['Professionnel', 'Chaleureux', 'Urgent', 'Exclusif'] },
      { name: 'offer', label: 'Offre spéciale', type: 'text', placeholder: 'Ex: -30% pendant 48h' }
    ],
    product: [
      { name: 'category', label: 'Catégorie', type: 'text', placeholder: 'Ex: Électronique, Beauté, Sport' },
      { name: 'audience', label: 'Client idéal', type: 'text', placeholder: 'Ex: Sportifs, Professionnels' },
      { name: 'usp', label: 'Avantage unique', type: 'text', placeholder: 'Ex: 14 jours d\'autonomie' },
      { name: 'price', label: 'Prix', type: 'text', placeholder: 'Ex: 299€' }
    ],
    contentIdeas: [
      { name: 'platform', label: 'Plateforme principale', type: 'select', options: ['Instagram', 'TikTok', 'YouTube', 'LinkedIn', 'Multi'] },
      { name: 'audience', label: 'Public visé', type: 'text', placeholder: 'Ex: Entrepreneurs débutants' },
      { name: 'frequency', label: 'Fréquence', type: 'select', options: ['Quotidienne', '3x/semaine', 'Hebdomadaire'] }
    ],
    caption: [
      { name: 'platform', label: 'Plateforme', type: 'select', options: ['Instagram', 'Facebook', 'LinkedIn', 'TikTok'] },
      { name: 'audience', label: 'Public visé', type: 'text', placeholder: 'Ex: Créatifs, Professionnels' },
      { name: 'tone', label: 'Ton souhaité', type: 'select', options: ['Engageant', 'Professionnel', 'Inspirant', 'Humoristique'] },
      { name: 'length', label: 'Longueur souhaitée', type: 'select', options: ['Courte (50-100 mots)', 'Moyenne (150-200 mots)', 'Longue (250+ mots)'] }
    ]
  };

  const handleGenerate = () => {
    setIsGenerating(true);
    setTimeout(() => {
      const generator = promptGenerators[selectedCategory];
      const prompt = generator(formData);
      setGeneratedPrompt(prompt);
      setIsGenerating(false);
      
      // Save to history
      const categoryData = categories.find(c => c.id === selectedCategory);
      onSavePrompt({
        id: Date.now(),
        categoryId: selectedCategory,
        category: categoryData.name,
        content: prompt,
        date: new Date().toLocaleString('fr-FR')
      });
    }, 1500);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedPrompt);
    setToastMessage('Prompt copié avec succès !');
    setShowToast(true);
  };

  const handleReset = () => {
    setFormData({});
    setGeneratedPrompt('');
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 mt-16">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center space-x-2 text-gray-400 hover:text-white mb-8 transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>Retour à l'accueil</span>
      </button>

      {!selectedCategory ? (
        /* Category Selection - Étape 1 */
        <div className="space-y-8 animate-fade-in">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-white mb-4">
              Que veux-tu créer ?
            </h2>
            <p className="text-gray-400 text-lg">
              Choisis le type de contenu que tu souhaites générer
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className="group bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300 hover:scale-105 text-left"
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${category.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  {category.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  {category.name}
                </h3>
                <p className="text-gray-400 text-sm">
                  {category.description}
                </p>
              </button>
            ))}
          </div>
        </div>
      ) : (
        /* Form and Generation - Étape 2-5 */
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Panel - Form */}
          <div className="space-y-6 animate-fade-in">
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  {categories.find(c => c.id === selectedCategory)?.icon}
                  <h3 className="text-xl font-bold text-white">
                    {categories.find(c => c.id === selectedCategory)?.name}
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setSelectedCategory(null);
                    handleReset();
                    setShowAdvanced(false);
                  }}
                  className="text-gray-400 hover:text-white text-sm"
                >
                  Changer
                </button>
              </div>

              {/* Champs essentiels - toujours visibles */}
              <div className="space-y-4 mb-6">
                {essentialFields[selectedCategory]?.map(field => (
                  <div key={field.name}>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {field.label}
                    </label>
                    {field.type === 'select' ? (
                      <select
                        value={formData[field.name] || ''}
                        onChange={(e) => setFormData({...formData, [field.name]: e.target.value})}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="" className="bg-slate-800">Sélectionne une option</option>
                        {field.options.map(opt => (
                          <option key={opt} value={opt} className="bg-slate-800">{opt}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={formData[field.name] || ''}
                        onChange={(e) => setFormData({...formData, [field.name]: e.target.value})}
                        placeholder={field.placeholder}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Section Personnaliser (facultatif) - Étape 3 */}
              <div className="border-t border-white/10 pt-4">
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="flex items-center space-x-2 text-purple-400 hover:text-purple-300 transition-colors mb-4"
                >
                  <span className="text-sm font-medium">Personnaliser (facultatif)</span>
                  {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>

                {showAdvanced && (
                  <div className="space-y-4 animate-fade-in">
                    {advancedFields[selectedCategory]?.map(field => (
                      <div key={field.name}>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          {field.label}
                        </label>
                        {field.type === 'select' ? (
                          <select
                            value={formData[field.name] || ''}
                            onChange={(e) => setFormData({...formData, [field.name]: e.target.value})}
                            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="" className="bg-slate-800">Sélectionne une option</option>
                            {field.options.map(opt => (
                              <option key={opt} value={opt} className="bg-slate-800">{opt}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={formData[field.name] || ''}
                            onChange={(e) => setFormData({...formData, [field.name]: e.target.value})}
                            placeholder={field.placeholder}
                            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Bouton principal - Étape 4 */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating || Object.keys(formData).length === 0}
                  className={`flex-1 flex items-center justify-center space-x-2 px-6 py-4 rounded-xl font-semibold transition-all ${
                    isGenerating || Object.keys(formData).length === 0
                      ? 'bg-gray-600 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:scale-105'
                  } text-white`}
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Génération en cours...</span>
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-5 h-5" />
                      <span>Générer mon prompt</span>
                    </>
                  )}
                </button>
                <button
                  onClick={handleReset}
                  className="px-6 py-4 bg-white/10 hover:bg-white/20 rounded-xl text-white transition-colors"
                  title="Réinitialiser"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Right Panel - Generated Prompt - Étape 5 */}
          <div className="lg:sticky lg:top-24 h-fit animate-fade-in">
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">Ton prompt prêt à utiliser</h3>
                {generatedPrompt && (
                  <div className="flex space-x-2">
                    <button
                      onClick={handleCopy}
                      className="flex items-center space-x-2 px-3 py-2 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg transition-colors text-purple-400 text-sm"
                      title="Copier le prompt"
                    >
                      <Copy className="w-4 h-4" />
                      <span>Copier</span>
                    </button>
                    <button
                      onClick={() => {/* Fonction modifier à implémenter */}}
                      className="flex items-center space-x-2 px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg transition-colors text-blue-400 text-sm"
                      title="Modifier le prompt"
                    >
                      <Edit3 className="w-4 h-4" />
                      <span>Modifier</span>
                    </button>
                    <button
                      onClick={() => {/* Fonction sauvegarder à implémenter */}}
                      className="flex items-center space-x-2 px-3 py-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg transition-colors text-green-400 text-sm"
                      title="Sauvegarder le prompt"
                    >
                      <Save className="w-4 h-4" />
                      <span>Sauvegarder</span>
                    </button>
                  </div>
                )}
              </div>

              <div className="bg-black/30 rounded-xl p-6 min-h-96 max-h-[600px] overflow-y-auto border border-white/10">
                {isGenerating ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader />
                  </div>
                ) : generatedPrompt ? (
                  <pre className="text-gray-300 whitespace-pre-wrap font-mono text-sm leading-relaxed">
                    {generatedPrompt}
                  </pre>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center space-y-4">
                    <Sparkles className="w-16 h-16 text-purple-400/30" />
                    <p className="text-gray-500 text-lg">
                      Remplis les champs et clique sur "Générer mon prompt"
                    </p>
                  </div>
                )}
              </div>

              {generatedPrompt && (
                <div className="mt-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <p className="text-sm text-purple-300">
                    💡 Copie ce prompt et utilise-le avec ChatGPT, Claude ou toute IA !
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <Toast
          message={toastMessage}
          type="success"
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
};

export default GeneratorPage;
